import React, { useMemo } from 'react';
import i18n from '../../../../../../i18n';
import { METRICS, fmt } from '../../../../data/metrics';
import type { DrillPanel, DriverDimension } from '../../../../types';
import { DrillTile } from '../DrillTile';
import { DrillShareCascade } from '../charts/DrillShareCascade';
import { drillRankColor } from '../charts/colors';
import { DriverBucketRows } from './DriverBucketRows';
import { DriverDimensionAdvanced } from './DriverDimensionCrux';
import { dedupeDimensions, dimFmtWithSample, isMutedBucket, sourceColor } from './driverUtils';
import { dimHeadline } from './driverWords';

function classifiedFootnote(dim: DriverDimension): string | null {
  if (dim.classified == null || dim.total == null) {
    return null;
  }
  if (dim.classified < dim.total) {
    return i18n.t('performance:drillUi.classifiedOf', {
      classified: fmt.num(dim.classified),
      total: fmt.num(dim.total),
    });
  }
  const shown = dim.shown ?? dim.classified;
  if (shown < dim.total) {
    return i18n.t('performance:drillUi.shownOf', {
      shown: fmt.num(shown),
      total: fmt.num(dim.total),
    });
  }
  return null;
}

/** Reservations behind a driver tile: the dimension's sample (every
 *  reservation in the window for rate / average dimensions, the drawn
 *  buckets' reservations for shares). The largest dimension speaks for the
 *  tile, since each dimension classifies the same window. */
export function driverTileN(dims: DriverDimension[]): number | null {
  let best: number | null = null;
  for (const dim of dims) {
    const n =
      dim.mode === 'share'
        ? dim.buckets.reduce((a, b) => a + (b.sample || 0), 0)
        : (dim.total ?? dim.buckets.reduce((a, b) => a + (b.sample || 0), 0));
    if (n > 0 && (best == null || n > best)) best = n;
  }
  return best;
}

function bucketColor(dim: DriverDimension, label: string, index: number): string {
  const bucket = dim.buckets.find(b => b.label === label);
  if (bucket && isMutedBucket(bucket)) return 'var(--line-2)';
  if (dim.id === 'source') return sourceColor(label);
  return drillRankColor(index, 6);
}

/** Correlates show up to six dimensions; the backend sends no more today. */
const MAX_DIMS = 6;

/** Reservations behind one card: the sample for a rate or average, the
 *  drawn buckets' reservations for a share. */
function dimN(dim: DriverDimension): number | null {
  const n =
    dim.mode === 'share'
      ? dim.buckets.reduce((a, b) => a + (b.sample || 0), 0)
      : (dim.total ?? dim.buckets.reduce((a, b) => a + (b.sample || 0), 0));
  return n > 0 ? n : null;
}

function DriverInspector({
  dims,
  metricKey,
  fmtVal,
  fmtVal2,
  secondaryLabel,
  compareLabel,
}: {
  dims: DriverDimension[];
  metricKey: string;
  fmtVal: (v: number) => string;
  fmtVal2?: (v: number) => string;
  secondaryLabel?: string;
  compareLabel?: string;
}) {
  return (
    <div className="dcrux-dim-grid">
      {dims.map(dim => (
        <DriverDimensionAdvanced
          key={dim.id}
          dim={dim}
          metricKey={metricKey}
          fmtVal={fmtVal}
          fmtVal2={fmtVal2}
          secondaryLabel={secondaryLabel}
          compareLabel={compareLabel}
        />
      ))}
    </div>
  );
}

/** True when this panel is the solo booking-lead-time share tile. */
export function isSoloLeadTimePanel(
  panel: Extract<DrillPanel, { kind: 'drivers' }>,
): boolean {
  const dims = panel.dimensions ?? [];
  return dims.length === 1 && dims[0]?.id === 'leadTime';
}

/** True for any drivers panel that breaks its metric down by several dimensions. */
export function isMultiDimPanel(
  panel: Extract<DrillPanel, { kind: 'drivers' }>,
): boolean {
  return (panel.dimensions?.length ?? 0) > 1;
}

/**
 * True when this panel is the drill metric's own correlates grid. A multi-dimension
 * panel about another metric (seat time on the Turns drill) is a lever tile instead.
 */
export function isCorrelatesPanel(
  panel: Extract<DrillPanel, { kind: 'drivers' }>,
  drillKey: string,
): boolean {
  return isMultiDimPanel(panel) && (panel.metricKey ?? drillKey) === drillKey;
}

/**
 * Inspector focus id that names the same tile across renders, metric tabs and
 * reloads: the panel's metric, its role and its dimension ids, never a list index.
 */
export function driversSectionId(
  panel: Extract<DrillPanel, { kind: 'drivers' }>,
  drillKey: string,
  role: DriversPanelRole,
): string {
  const dimIds = (panel.dimensions ?? []).map(d => d.id).join('+');
  return `drivers:${panel.metricKey ?? drillKey}:${role}:${dimIds}`;
}

export type DriversPanelRole = 'correlates' | 'lever' | 'single';

/**
 * Correlates grid: drop booking-source (already covered by Source mix tile)
 * and slot lead-time into that slot when available.
 */
function composeCorrelatesDims(
  dims: DriverDimension[],
  leadTimeDim?: DriverDimension | null,
): DriverDimension[] {
  const withoutSource = dims.filter(d => d.id !== 'source');
  if (!leadTimeDim) return withoutSource;
  const withoutLead = withoutSource.filter(d => d.id !== 'leadTime');
  // Prefer daypart → leadTime → remaining (guest type, party size, …)
  const daypart = withoutLead.find(d => d.id === 'daypart');
  const rest = withoutLead.filter(d => d.id !== 'daypart');
  return [daypart, leadTimeDim, ...rest].filter(Boolean) as DriverDimension[];
}

/**
 * One drivers panel as a deck tile: a single dimension (lead time), the drill
 * metric's correlates grid, or another metric's lever grid (seat time on Turns).
 */
export function DriversPanelSection({
  panel,
  metricKey,
  fmtVal,
  fmtVal2,
  primaryLabel,
  secondaryLabel,
  sectionId,
  role = 'correlates',
  /** Fold solo lead-time into correlates in place of booking source. */
  foldLeadTimeDim,
  compareLabel,
  span = 6,
}: {
  panel: Extract<DrillPanel, { kind: 'drivers' }>;
  metricKey: string;
  fmtVal: (v: number) => string;
  fmtVal2?: (v: number) => string;
  primaryLabel?: string;
  /** Column name for the paired metric's figures in Details. */
  secondaryLabel?: string;
  sectionId: string;
  /** Correlates drop the source dimension (Source mix covers it); a lever grid keeps every dimension. */
  role?: DriversPanelRole;
  foldLeadTimeDim?: DriverDimension | null;
  /** Legend label for compare chips, e.g. "vs 3 Jun – 30 Jun". */
  compareLabel?: string;
  /** Half-width next to outlets; full-width when the outlet card is hidden. */
  span?: 6 | 12;
}) {
  const dims = useMemo(() => {
    const base = dedupeDimensions(panel.dimensions ?? []);
    if (base.length > 1 && role === 'correlates') {
      return composeCorrelatesDims(base, foldLeadTimeDim);
    }
    return base;
  }, [panel.dimensions, foldLeadTimeDim, role]);

  const dimMetricKey = panel.metricKey ?? metricKey;
  const inspector = useMemo(
    () => (
      <DriverInspector
        dims={dims}
        metricKey={dimMetricKey}
        fmtVal={fmtVal}
        fmtVal2={fmtVal2}
        secondaryLabel={secondaryLabel}
        compareLabel={compareLabel}
      />
    ),
    [dims, dimMetricKey, fmtVal, fmtVal2, secondaryLabel, compareLabel],
  );

  if (!dims.length) return null;

  const mode = dims[0].mode;
  const solo = dims.length === 1;
  const short =
    primaryLabel ?? METRICS[dimMetricKey]?.short ?? dimMetricKey;
  const shareMeta =
    dimMetricKey === 'occupancy'
      ? i18n.t('performance:drillUi.shareOfOccupancy')
      : i18n.t('performance:drillUi.contributionOf', { metric: short });
  // What the figures are: a share of the metric, how often it happens in
  // each group, or the group's average, each against the venue's own.
  const modeMeta =
    mode === 'share'
      ? shareMeta
      : mode === 'rate'
        ? i18n.t('performance:drillUi.metaRate')
        : i18n.t('performance:drillUi.metaAvg');
  const withWindow = (rest: string) =>
    panel.windowNote ? `${panel.windowNote} · ${rest}` : rest;
  const nDrivers = driverTileN(dims.slice(0, MAX_DIMS));
  const tileN =
    nDrivers != null
      ? i18n.t('performance:drillUi.nReservations', {
          count: nDrivers,
          n: fmt.num(nDrivers),
        })
      : undefined;

  if (solo) {
    const dim = dims[0];
    const coverage = classifiedFootnote(dim);
    const headline = mode === 'share' ? null : dimHeadline(dim, fmtVal, dimMetricKey);

    return (
      <DrillTile
        sectionId={sectionId}
        span={span}
        title={panel.title}
        meta={withWindow(coverage ? `${modeMeta} · ${coverage}` : modeMeta)}
        inspector={inspector}
        n={tileN}
      >
        {mode === 'share' ? (
          <DrillShareCascade
            rows={dim.buckets.map((b, i) => ({
              name: b.label,
              value: Math.abs(b.value),
              color: bucketColor(dim, b.label, i),
              compareValue: b.compareValue,
              vsComparePct: b.vsComparePct,
            }))}
            fmtVal={v => dimFmtWithSample(dim, v, fmtVal)}
            compareLabel={compareLabel}
          />
        ) : (
          <div className="dbr-solo">
            {headline ? <p className="ddrill-say">{headline}</p> : null}
            <DriverBucketRows
              dim={dim}
              fmtVal={fmtVal}
              metricKey={dimMetricKey}
              compareLabel={compareLabel}
              size="lg"
            />
          </div>
        )}
      </DrillTile>
    );
  }

  // Grid of labelled mini charts, every bucket of up to six dimensions.
  const shown = dims.slice(0, MAX_DIMS);
  const coverageNote = shown
    .map(classifiedFootnote)
    .find((note): note is string => !!note);
  // The cards draw the drill's own metric; a paired metric lives in the
  // Details inspector, so the meta does not promise it here.
  const baseMeta = modeMeta;

  return (
    <DrillTile
      sectionId={sectionId}
      span={span}
      title={panel.title}
      meta={withWindow(coverageNote ? `${baseMeta} · ${coverageNote}` : baseMeta)}
      inspector={inspector}
      n={tileN}
    >
      <div className="ddrill-correlates-wrap">
        <div className="ddrill-correlates">
          {shown.map(dim => {
            // The sentence names only buckets the server found significant.
            const headline = dimHeadline(dim, fmtVal, dimMetricKey);
            const coverage = classifiedFootnote(dim);
            const n = dimN(dim);
            return (
              <div className="ddrill-mini" key={dim.id}>
                <div className="ddrill-mini-head">
                  <div className="ddrill-mini-row">
                    <div className="ddrill-mini-lab">{dim.label}</div>
                    {n != null ? (
                      <div className="ddrill-mini-n">
                        {i18n.t('performance:drillUi.reservationsCount', {
                          count: n,
                          formatted: fmt.num(n),
                        })}
                      </div>
                    ) : null}
                  </div>
                  {headline ? <p className="ddrill-say">{headline}</p> : null}
                </div>
                <DriverBucketRows
                  dim={dim}
                  fmtVal={fmtVal}
                  metricKey={dimMetricKey}
                  compareLabel={compareLabel}
                />
                {coverage ? <p className="ddrill-mini-foot">{coverage}</p> : null}
              </div>
            );
          })}
        </div>
      </div>
    </DrillTile>
  );
}
